import React, { CSSProperties, ReactNode, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';
import { FormGroup } from '../form/FormGroup';
import InputError from '../form/InputError';
import InputLabel from '../form/InputLabel';
import { InfoHint } from '../hint/InfoHint';
import ScrollToError from './ScrollToError';
import { getError } from './utils';

type InputType =
  | 'email'
  | 'password'
  | 'select'
  | 'textarea'
  | 'time'
  | 'number'
  | 'checkbox'
  | 'url'
  | 'tel'
  | 'hidden';

type Props = InputProps | TextAreaProps | NumberProps;

type InputProps = {
  label?: string;
  name: string;
  placeholder?: string;
  type?: InputType;
  children?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  labelComponent?: ReactNode;
  autoFocus?: boolean;
  size?: 'sm' | 'lg' | 'md';
  className?: string;
  hint?: ReactNode;
  onChange?: (value: string) => void;
  style?: CSSProperties;
  defaultValue?: string;
  readOnly?: boolean;
};

type TextAreaProps = InputProps & {
  type: 'textarea';
  minRows?: number;
  maxRows?: number;
  maxLength?: number;
};

type NumberProps = InputProps & {
  type: 'number';
  min?: number;
  max?: number;
  step?: string;
};

const formControlType = (type?: InputType) => {
  switch (type) {
    case 'select':
    case 'textarea':
      return { as: type };
    default: {
      return { type: type };
    }
  }
};

type ControlledInputProps = Omit<
  InputProps,
  'label' | 'labelComponent' | 'required'
>;

type ControlledTextAreaProps = Omit<
  TextAreaProps,
  'label' | 'labelComponent' | 'required'
>;

export const ControlledInput: React.FC<
  React.PropsWithChildren<ControlledInputProps | ControlledTextAreaProps>
> = ({
  name,
  type,
  disabled,
  className,
  onChange,
  readOnly,
  defaultValue,
  placeholder,
  children,
  autoFocus,
  size,
  style,
  ...rest
}) => {
  const {
    control,
    formState: { isSubmitting, errors },
  } = useFormContext();

  const error = getError(name, errors);

  const inputref = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const isReadOnly = isSubmitting || readOnly;
  const minRows =
    'minRows' in rest && typeof rest.minRows === 'number'
      ? rest.minRows
      : undefined;
  const maxRows =
    'maxRows' in rest && typeof rest.maxRows === 'number'
      ? rest.maxRows
      : undefined;
  const maxLength =
    'maxLength' in rest && typeof rest.maxLength === 'number'
      ? rest.maxLength
      : undefined;
  const min =
    'min' in rest && typeof rest.min === 'number' ? rest.min : undefined;
  const max =
    'max' in rest && typeof rest.max === 'number' ? rest.max : undefined;
  const step =
    'step' in rest && typeof rest.step === 'string' ? rest.step : undefined;
  const textareaStyle = {
    ...style,
    height: typeof style?.height === 'number' ? style.height : undefined,
  };

  useEffect(() => {
    if (autoFocus) {
      inputref.current?.focus();
    }
  }, [autoFocus]);

  if (type === 'hidden') {
    return (
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => (
          <input
            {...field}
            id={name}
            value={field.value ?? ''}
            onChange={(event) => {
              field.onChange(event);
              onChange?.(event.target.value);
            }}
            type="hidden"
            disabled={disabled}
            className={className}
            style={style}
          />
        )}
      />
    );
  }

  return (
    <>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) =>
          type === 'textarea' ? (
            <TextareaAutosize
              {...field}
              ref={(element) => {
                field.ref(element);
                inputref.current = element;
              }}
              id={name}
              value={field.value ?? ''}
              onChange={(event) => {
                field.onChange(event);
                onChange?.(event.target.value);
              }}
              disabled={disabled}
              readOnly={isReadOnly}
              placeholder={placeholder}
              autoFocus={autoFocus}
              minRows={minRows}
              maxRows={maxRows}
              maxLength={maxLength}
              style={textareaStyle}
              className={[
                'form-control',
                className,
                error ? 'is-invalid' : '',
              ].join(' ')}
            />
          ) : (
            <Form.Control
              {...field}
              id={name}
              value={field.value ?? ''}
              onChange={(event) => {
                field.onChange(event);
                onChange?.(event.target.value);
              }}
              disabled={disabled}
              isInvalid={!!error}
              readOnly={isReadOnly}
              placeholder={placeholder}
              autoFocus={autoFocus}
              size={size === 'md' ? undefined : size}
              style={style}
              min={min}
              max={max}
              step={step}
              className={className}
              {...formControlType(type)}
              ref={(element: HTMLInputElement | HTMLTextAreaElement | null) => {
                field.ref(element);
                inputref.current = element;
              }}>
              {children}
            </Form.Control>
          )
        }
      />
      <InputError error={error} />
    </>
  );
};

export const Input: React.FC<React.PropsWithChildren<Props>> = ({
  label,
  name,
  required,
  hint,
  labelComponent,
  ...rest
}) => {
  const ref = useRef(null);

  return React.useMemo(() => {
    if (rest.type === 'hidden') {
      return <ControlledInput name={name} {...rest} />;
    }

    return (
      <FormGroup ref={ref}>
        {labelComponent ? (
          labelComponent
        ) : (
          <InputLabel
            inputId={name}
            label={label}
            required={required}
            hint={
              hint ? (
                <InfoHint className="mt-2" text={String(hint)} />
              ) : undefined
            }
          />
        )}
        <ControlledInput name={name} {...rest} />
        <ScrollToError name={name} inputContainerRef={ref} />
      </FormGroup>
    );
  }, [label, labelComponent, name, required, rest, ref, hint]);
};
